import ReactECharts from "echarts-for-react";
import { ClientGrowthMetric } from "@/services/metrics";
import { Loader2 } from "lucide-react";

interface ClientGrowthChartProps {
  data: ClientGrowthMetric[];
  isLoading: boolean;
}

export function ClientGrowthChart({ data, isLoading }: ClientGrowthChartProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[300px]">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[300px] text-muted-foreground text-sm">
        Sin datos para el período seleccionado
      </div>
    );
  }

  const dates = data.map((d) => d.snapshotDate);

  const option = {
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "cross", label: { backgroundColor: "#3B82F6" } },
      backgroundColor: "#fff",
      borderColor: "#e2e8f0",
      borderWidth: 1,
      textStyle: { color: "#1e293b", fontSize: 12 },
    },
    legend: {
      data: ["Total Clientes", "Activos", "Nuevos", "Perdidos"],
      bottom: 0,
      textStyle: { color: "#64748b", fontSize: 11 },
      icon: "roundRect",
      itemWidth: 10,
      itemHeight: 6,
    },
    grid: { left: "3%", right: "4%", bottom: "15%", top: "6%", containLabel: true },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: dates,
      axisLine: { lineStyle: { color: "#e2e8f0" } },
      axisLabel: { color: "#94a3b8", fontSize: 11 },
    },
    yAxis: {
      type: "value",
      axisLine: { show: false },
      splitLine: { lineStyle: { color: "#f1f5f9", type: "dashed" } },
      axisLabel: { color: "#94a3b8", fontSize: 11 },
    },
    series: [
      {
        name: "Total Clientes",
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 5,
        data: data.map((d) => d.totalClients),
        lineStyle: { color: "#3B82F6", width: 2.5 },
        itemStyle: { color: "#3B82F6" },
        areaStyle: {
          color: {
            type: "linear",
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: "rgba(59,130,246,0.18)" },
              { offset: 1, color: "rgba(59,130,246,0)" },
            ],
          },
        },
      },
      {
        name: "Activos",
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 5,
        data: data.map((d) => d.activeClients),
        lineStyle: { color: "#22C55E", width: 2.5 },
        itemStyle: { color: "#22C55E" },
        areaStyle: {
          color: {
            type: "linear",
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: "rgba(34,197,94,0.12)" },
              { offset: 1, color: "rgba(34,197,94,0)" },
            ],
          },
        },
      },
      {
        name: "Nuevos",
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 5,
        data: data.map((d) => d.newClients),
        lineStyle: { color: "#F59E0B", width: 2 },
        itemStyle: { color: "#F59E0B" },
      },
      {
        name: "Perdidos",
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: 5,
        data: data.map((d) => d.lostClients),
        lineStyle: { color: "#EF4444", width: 2 },
        itemStyle: { color: "#EF4444" },
      },
    ],
  };

  return <ReactECharts option={option} style={{ height: 300 }} opts={{ renderer: "svg" }} />;
}
